import { Stack } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { Children, isValidElement, useMemo } from 'react';
import {
    DesignSystemAlertSize,
    DesignSystemAlertStacking,
    DesignSystemButtonColorScheme,
} from '../../theme/design-system/types';
import { SlotProvider } from '../../utils/SlotContext';
import { Slots } from '../../utils/Slots';
import { AlertComponent, AlertProps } from './Alert.types';
import { AlertClose } from './AlertClose';
import { AlertActions } from './AlertActions';
import { AlertContent } from './AlertContent';
import { AlertTitle } from './AlertTitle';
import { buildAlertGrid } from './gridBuilder';
import {
    getAlertGapStyles,
    getBorderStyles,
    getColorStyles,
    getSizeStyles,
} from './styleUtils';
import { AlertIcon } from '@upheal/design-system/components/Alert/AlertIcon';

const defaultSize: DesignSystemAlertSize = 'sm';
const defaultColorScheme: DesignSystemButtonColorScheme = 'neutral';
const defaultStacking: DesignSystemAlertStacking = 'inline';

/**
 * Alert component for displaying messages to the user.
 */
const AlertBase = (props: AlertProps) => {
    const {
        children,
        size = defaultSize,
        colorScheme = defaultColorScheme,
        stacking = defaultStacking,
        onClose,
    } = props;
    const theme = useTheme();

    const registeredComponents: Set<AlertComponent> = useMemo(() => {
        const components = new Set<AlertComponent>();

        if (onClose) {
            components.add('close');
        }

        Children.forEach(children, (child) => {
            if (!isValidElement(child)) {
                return;
            }

            switch (child.type) {
                case AlertActions: {
                    components.add('actions');
                    break;
                }
                case AlertContent: {
                    components.add('content');
                    break;
                }
                case AlertTitle: {
                    components.add('title');
                    break;
                }
            }
        });

        return components;
    }, [children, onClose]);

    const { sizeStyles, colorStyles, gapStyles, wrapperStyles } =
        useMemo(() => {
            const sizeStyles = getSizeStyles({ size });
            const colorStyles = getColorStyles({ theme, colorScheme });
            const gapStyles = getAlertGapStyles({ size, stacking });
            const borderStyles = getBorderStyles({ theme, colorScheme });

            const wrapperStyles: SxProps<Theme> = {
                paddingX: sizeStyles.paddingX,
                paddingY: sizeStyles.paddingY,
                backgroundColor: colorStyles.backgroundColor,
                borderRadius: theme.borders.ds.borderRadius.sm,
                border: borderStyles.border,
            };

            return {
                sizeStyles,
                colorStyles,
                gapStyles,
                wrapperStyles,
            };
        }, [size, theme, colorScheme, stacking]);

    const slots: Slots = useMemo(
        () => ({
            icon: {
                color: colorStyles.iconColor,
                size: sizeStyles.iconSize,
            },
            alert: {
                size,
                colorScheme,
                stacking,
                columnGap: gapStyles.columnGap,
                rowGap: gapStyles.rowGap,
                contentGap: gapStyles.contentGap,
                actionsGap: gapStyles.actionsGap,
                closeExtraGap: gapStyles.closeExtraGap,
                titleGap: gapStyles.titleGap,
                contentToActionsGap: gapStyles.contentToActionsGap,
                registeredComponents,
            },
            button: {
                colorScheme: 'neutral',
                variant: 'link',
                size: size === 'xs' ? 'xs' : 'sm',
            },
        }),
        [
            colorStyles,
            colorScheme,
            size,
            sizeStyles.iconSize,
            gapStyles,
            stacking,
            registeredComponents,
        ],
    );

    const gridTemplate = useMemo(() => {
        return buildAlertGrid(registeredComponents, stacking);
    }, [registeredComponents, stacking]);

    return (
        <SlotProvider value={slots}>
            <Stack
                sx={{
                    display: 'grid',
                    alignItems: stacking === 'inline' ? 'center' : 'start',
                    columnGap: gapStyles.columnGap,
                    rowGap: gapStyles.rowGap,
                    gridTemplate,
                    ...wrapperStyles,
                }}
            >
                {children}
                {onClose && <AlertClose onClose={onClose} />}
            </Stack>
        </SlotProvider>
    );
};

export const Alert = Object.assign(AlertBase, {
    Title: AlertTitle,
    Icon: AlertIcon,
    Content: AlertContent,
    Actions: AlertActions,
    Close: AlertClose,
});
